import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Mail, Lock, User, ArrowRight, Gift, Building, Globe, Briefcase, MessageSquare, CheckCircle2, Loader2, KeyRound, RefreshCw } from "lucide-react";
import axios from "axios";

const COUNTRIES = [
  "Australia", "Canada", "India", "New Zealand", "Singapore", 
  "United Arab Emirates", "United Kingdom", "United States", "Other"
];

const USER_TYPES = [
  { value: "hotel_manager", label: "Hotel/Motel Manager" },
  { value: "owner", label: "Owner" },
  { value: "asset_manager", label: "Asset Manager" },
  { value: "franchisor", label: "Franchisor" },
  { value: "mortgage_agent", label: "Mortgage Agent" },
  { value: "banker", label: "Banker/Bank" }
];

const REFERRAL_SOURCES = [
  { value: "google", label: "Google Search" },
  { value: "social_media", label: "Social Media" },
  { value: "referral", label: "Friend/Colleague Referral" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "industry_event", label: "Industry Event" },
  { value: "other", label: "Other" }
];

export default function RegisterPage() {
  const [step, setStep] = useState(1); // 1: Profile, 2: Password, 3: Verification
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    fullName: "",
    companyName: "",
    country: "",
    email: "",
    userType: "",
    referralSource: "",
    password: "",
    confirmPassword: "",
    verificationCode: ""
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const inviteCode = searchParams.get("invite");
  const hasInvite = !!inviteCode;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    if (!formData.fullName.trim()) {
      toast.error("Please enter your full name");
      return false;
    }
    if (!formData.country) {
      toast.error("Please select your country");
      return false;
    }
    if (!formData.email.trim()) {
      toast.error("Please enter your email");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!formData.userType) {
      toast.error("Please select what describes you best");
      return false;
    }
    if (!formData.referralSource) {
      toast.error("Please let us know how you heard about us");
      return false;
    }
    return true;
  };

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (!validateStep1()) return;
    setStep(2);
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      // Register - returns token directly (no verification needed for testing)
      const response = await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        name: formData.fullName,
        company_name: formData.companyName,
        country: formData.country,
        user_type: formData.userType,
        referral_source: formData.referralSource,
        invite_code: inviteCode
      });
      
      // Store token and redirect to dashboard
      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token);
        
        if (hasInvite) {
          toast.success("Welcome! You have 3 days of free Premium access!");
        } else {
          toast.success("Account created successfully! Welcome to HotelBudget.");
        }
        
        // Redirect to dashboard
        window.location.href = "/";
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    
    if (!formData.verificationCode || formData.verificationCode.length !== 6) {
      toast.error("Please enter the 6-digit verification code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-email`, {
        email: formData.email,
        code: formData.verificationCode
      });
      
      // Store token and redirect
      localStorage.setItem("token", response.data.access_token);
      
      if (hasInvite) {
        toast.success("Welcome! You have 30 days of free Premium access!");
      } else {
        toast.success("Email verified! Welcome to HotelBudget.");
      }
      
      // Reload to update auth state
      window.location.href = "/";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification?email=${encodeURIComponent(formData.email)}`);
      toast.success("Verification code resent! Check your email.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend code");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Gradient Background */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#4B5E40] via-[#5A7A5A] to-[#8FA385] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-50" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <h1 className="text-4xl font-extrabold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Join HotelBudget Today
          </h1>
          <p className="text-lg text-white/80 max-w-md mb-8">
            Create professional hotel budgets in minutes. Join hundreds of hospitality professionals who&apos;ve simplified their financial planning.
          </p>
          
          <div className="space-y-4">
            {[
              "One-click budget creation",
              "5-year cash flow forecasting",
              "Industry-standard benchmarks",
              "Bank-ready professional reports"
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#C9A227]" />
                <span className="text-white/90">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Register form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-8 bg-slate-50">
        <div className="w-full max-w-lg">
          <Link to="/welcome" className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#4B5E40] to-[#8FA385] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                HotelBudget
              </h2>
              <p className="text-sm text-slate-500">Financial Planning Suite</p>
            </div>
          </Link>

          <Card className="border-slate-200 shadow-xl rounded-2xl">
            {hasInvite && (
              <div className="bg-gradient-to-r from-emerald-50 to-[#F5EDCE] border-b border-emerald-200 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-800">You&apos;ve been invited!</p>
                    <p className="text-sm text-emerald-600">Get 3 days of free Premium access</p>
                  </div>
                </div>
              </div>
            )}
            
            <CardHeader className="space-y-1 pb-4">
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-[#4B5E40] text-white' : 'bg-slate-200 text-slate-500'}`}>
                  1
                </div>
                <div className="h-1 w-12 rounded bg-slate-200">
                  <div className={`h-full rounded bg-[#4B5E40] transition-all duration-300 ${step >= 2 ? 'w-full' : 'w-0'}`} />
                </div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-[#4B5E40] text-white' : 'bg-slate-200 text-slate-500'}`}>
                  2
                </div>
              </div>
              <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {step === 1 ? "Tell us about yourself" : "Create your password"}
              </CardTitle>
              <CardDescription>
                {step === 1 ? "We'll personalize your experience" : "Secure your account"}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {step === 1 ? (
                <form onSubmit={handleStep1Submit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName" className="text-slate-700">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="fullName"
                          type="text"
                          placeholder="John Smith"
                          value={formData.fullName}
                          onChange={(e) => handleChange("fullName", e.target.value)}
                          className="pl-10"
                          data-testid="register-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="companyName" className="text-slate-700">
                        Company Name
                      </Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="companyName"
                          type="text"
                          placeholder="Optional"
                          value={formData.companyName}
                          onChange={(e) => handleChange("companyName", e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-slate-700">
                        Country <span className="text-red-500">*</span>
                      </Label>
                      <Select value={formData.country} onValueChange={(v) => handleChange("country", v)}>
                        <SelectTrigger className="w-full">
                          <Globe className="w-4 h-4 text-slate-400 mr-2" />
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700">
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="name@company.com"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          className="pl-10"
                          data-testid="register-email-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700">
                      What describes you best? <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.userType} onValueChange={(v) => handleChange("userType", v)}>
                      <SelectTrigger className="w-full">
                        <Briefcase className="w-4 h-4 text-slate-400 mr-2" />
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                      <SelectContent>
                        {USER_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700">
                      How did you hear about us? <span className="text-red-500">*</span>
                    </Label>
                    <Select value={formData.referralSource} onValueChange={(v) => handleChange("referralSource", v)}>
                      <SelectTrigger className="w-full">
                        <MessageSquare className="w-4 h-4 text-slate-400 mr-2" />
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        {REFERRAL_SOURCES.map((source) => (
                          <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-[#4B5E40] to-[#8FA385] hover:from-[#3C4C33] hover:to-[#5A7A5A] text-white py-6 rounded-xl font-semibold"
                    data-testid="register-continue-btn"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              ) : step === 2 ? (
                <form onSubmit={handleStep2Submit} className="space-y-4">
                  <div className="bg-slate-50 rounded-xl p-4 mb-4">
                    <p className="text-sm text-slate-600">Creating account for:</p>
                    <p className="font-semibold text-slate-900">{formData.email}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-700">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => handleChange("password", e.target.value)}
                        className="pl-10"
                        data-testid="register-password-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500">Must be at least 6 characters</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-slate-700">
                      Confirm Password <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="••••••••"
                        value={formData.confirmPassword}
                        onChange={(e) => handleChange("confirmPassword", e.target.value)}
                        className="pl-10"
                        data-testid="register-confirm-password-input"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 py-6 rounded-xl"
                      onClick={() => setStep(1)}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-[#4B5E40] to-[#8FA385] hover:from-[#3C4C33] hover:to-[#5A7A5A] text-white py-6 rounded-xl font-semibold"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Create Account
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              ) : null}

              {/* Divider - show on step 1 only */}
              {step === 1 && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-500">Or continue with</span>
                    </div>
                  </div>

                  {/* Google Sign-Up Button */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full py-6 rounded-xl font-medium border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all"
                    onClick={() => {
                      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                      const redirectUrl = window.location.origin + '/';
                      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                    }}
                    data-testid="google-signup-btn"
                  >
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Sign up with Google
                  </Button>
                </>
              )}
              
              <div className="mt-6 text-center">
                <p className="text-sm text-slate-600">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="font-semibold text-[#4B5E40] hover:text-[#3C4C33]"
                    data-testid="login-link"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
